// Spielen gegen Bots (Stockfish, Maia, Rodent-Persönlichkeiten, siehe playbots.ts), Steuerung:
// Regeln und Notation mit chessops, Lernhilfen in playcoach.ts, Zugablauf in playturn.ts.
// Die Ansicht steht in views/play.ts.
import { chessgroundDests } from 'chessops/compat';
import type { Move } from 'chessops/types';
import { parseSquare } from 'chessops/util';
import type { Key } from '@lichess-org/chessground/types';
import { t } from './i18n';
import { api } from './api';
import { DEFAULT_BOT, botName, normalize, type BotChoice } from './playbots';
import { PlayCoach } from './playcoach';
import { ending } from './playend';
import { PlayGame } from './playgame';
import type { GuessGame } from './guessgame';
import { Training } from './playtrain';
import type { PgnResult } from './playpgn';
import { afterOwnMove, engineMove, takeBack, takeBackPlies } from './playturn';
import { loadPref, savePref } from './prefs';
import type { PromotionRole } from './views/promotion';

export type Side = 'white' | 'black' | 'random';

export class PlayCtrl {
  bot: BotChoice = normalize({ ...DEFAULT_BOT, stockfishElo: loadPref('playElo', 1500), ...loadPref('playBot', {}) });
  /** welche Bots der Server hat; bis zur Antwort nur Stockfish */
  available = { stockfish: true, maia: false, rodent: false };
  side: Side = loadPref('playSide', 'white');
  started = false;
  me: 'white' | 'black' = 'white';
  /** Stellung und Züge der laufenden Partie */
  board = new PlayGame();
  thinking = false;
  result: string | null = null;
  resigned = false;
  /** Ergebnis für die PGN, unabhängig von der Sprache der Anzeige */
  outcome: PgnResult | null = null;
  error = '';
  saved = false;
  flipped = false;
  /** wartet auf die Wahl der Umwandlungsfigur */
  promotion: { from: number; to: number; last: [Key, Key] } | null = null;
  /** Zähler der Partie; ändert er sich, sind laufende Antworten veraltet */
  game = 0;
  readonly coach: PlayCoach;
  /** „Lerne aus Fehlern“ nach der Partie */
  train: Training | null = null;
  /** „Spiel wie …“: Partie der Bibliothek raten; solange gesetzt, zeigt die Ansicht nur das */
  guess: GuessGame | null = null;
  /** eigene Startstellung statt der Grundstellung (ab hier weiterspielen) */
  start: { fen: string; label: string } | null = null;

  constructor(readonly redraw: () => void) {
    this.coach = new PlayCoach(redraw);
  }

  setBot(change: Partial<BotChoice>) {
    this.bot = normalize({ ...this.bot, ...change });
    savePref('playBot', this.bot);
    this.redraw();
  }

  async loadBots() {
    try {
      this.available = await api.bots();
      if (!this.available[this.bot.kind]) this.bot = { ...this.bot, kind: 'stockfish' };
    } catch {
      // ohne Antwort bleibt Stockfish
    }
    this.redraw();
  }

  setSide(s: Side) {
    this.side = s;
    savePref('playSide', s);
    this.redraw();
  }

  newGame() {
    this.game++;
    this.me = this.side === 'random' ? (Math.random() < 0.5 ? 'white' : 'black') : this.side;
    this.board = new PlayGame(this.start?.fen);
    Object.assign(this, { flipped: false, result: null, resigned: false, outcome: null, error: '', saved: false, thinking: false, promotion: null, train: null, started: true });
    this.coach.reset();
    void this.coach.lookUp(this.fen, 0);
    const end = ending(this.pos, this.me, 1); // Startstellung schon matt oder patt
    if (end) Object.assign(this, { result: end.text, outcome: end.outcome });
    this.redraw();
    if (!end && this.pos.turn !== this.me) void engineMove(this);
  }

  get pos() {
    return this.board.pos;
  }

  get moves() {
    return this.board.moves;
  }

  get fen() {
    return this.board.fen;
  }

  dests() {
    const waiting = this.thinking || this.coach.checking || this.coach.pending;
    if (!this.started || this.result || waiting || this.pos.turn !== this.me) return new Map();
    return chessgroundDests(this.pos);
  }

  /** Zug auf die Partie anwenden (eigener oder des Gegners) */
  apply(move: Move, last: [Key, Key]) {
    if (!this.board.play(move, last)) return false;
    const end = ending(this.pos, this.me, this.board.repetitions);
    if (end) {
      this.result = end.text;
      this.outcome = end.outcome;
    }
    this.coach.moved();
    void this.coach.lookUp(this.fen, this.moves.length);
    return true;
  }

  userMove = (orig: Key, dest: Key) => {
    const from = parseSquare(orig)!, to = parseSquare(dest)!;
    const piece = this.pos.board.get(from);
    if (piece?.role === 'pawn' && (dest[1] === '8' || dest[1] === '1')) {
      this.promotion = { from, to, last: [orig, dest] };
      this.redraw();
      return;
    }
    this.played({ from, to }, [orig, dest]);
  };

  /** Umwandlungsfigur gewählt; null = abgebrochen, das Brett springt zurück */
  promote(role: PromotionRole | null) {
    const p = this.promotion;
    this.promotion = null;
    const roles = { q: 'queen', r: 'rook', b: 'bishop', n: 'knight' } as const;
    if (p && role) this.played({ from: p.from, to: p.to, promotion: roles[role] }, p.last);
    else this.redraw();
  }

  private played(move: Move, last: [Key, Key]) {
    const before = this.fen;
    const ok = this.apply(move, last);
    this.redraw();
    if (ok) void afterOwnMove(this, before, last);
  }

  get canTakeBack() {
    return takeBackPlies(this) > 0;
  }

  takeBack = () => takeBack(this);
  /** nach einem gezeigten Fehler: den Zug zurücknehmen und neu ziehen */
  retry = () => takeBack(this);

  /** nach einem gezeigten Fehler: trotzdem weiterspielen */
  carryOn() {
    this.coach.pending = null;
    void engineMove(this);
  }

  askHint() {
    return this.coach.askHint(this.fen).catch(e => ((this.error = (e as Error).message), this.redraw()));
  }

  startTraining() {
    if (this.coach.slips.length) this.train = new Training(this.coach.slips, this.redraw);
    this.redraw();
  }

  stopTraining() {
    this.train = null;
    this.redraw();
  }

  resign() {
    if (!this.started || this.result) return;
    this.game++; // laufende Engine-Antwort verwerfen
    Object.assign(this, { thinking: false, resigned: true, outcome: this.me === 'white' ? '0-1' : '1-0' });
    Object.assign(this.coach, { pending: null, checking: false });
    this.result = t('Aufgegeben. {n} gewinnt.', { n: botName(this.bot) });
    this.redraw();
  }
}
